// Data types for cars (content JSON) and the simulation's per-step input/output.
// Coordinate convention (car local and world): +Y up, car forward = +Z, car left = +X.
// Positive steering angle at the road wheel = turn to the LEFT (positive yaw about +Y).

export type Vec3Tuple = [number, number, number];

export type SurfaceId =
  | 'tarmac'
  | 'concrete'
  | 'gravel'
  | 'loose'
  | 'mud'
  | 'wet'
  | 'water'
  | 'grass'
  | 'rock';

export interface SuspensionSpec {
  /** Total wheel travel (m) from full droop to bump. */
  travel: number;
  /** Wheel-rate spring stiffness (N/m). */
  springRate: number;
  /** Spring force at full droop (N); sets static ride height together with springRate. */
  preload: number;
  /** Damping (N·s/m at the wheel) for compression (bump) and extension (rebound), low/high speed. */
  bumpDamping: number;
  reboundDamping: number;
  /** Knee velocity (m/s) above which the high-speed damping fraction applies. */
  dampingKnee: number;
  highSpeedFactor: number;
  /** Bump-stop engages over the last bumpStopLength metres of compression travel. */
  bumpStopLength: number;
  bumpStopRate: number;
  /** Anti-roll bar wheel-rate stiffness (N/m of left-right travel difference). */
  antiRollRate: number;
}

export interface WheelSpec {
  /** Wheel centre at full droop in car local coordinates (m), relative to the car origin. */
  position: Vec3Tuple;
  axle: 'front' | 'rear';
  side: 'left' | 'right';
}

export interface TyreSpec {
  radius: number;
  width: number;
  /** Rotational inertia of wheel + tyre + brake disc + hub (kg·m²). */
  inertia: number;
  /** Relaxation lengths (m). */
  relaxationLong: number;
  relaxationLat: number;
  /** Load sensitivity: fractional µ loss per unit of (Fz/Fz_nominal - 1). */
  loadSensitivity: number;
  nominalLoad: number;
  /** Per-surface multipliers for this compound on peak µ and peak slip. Missing = 1. */
  surfaceGrip: Partial<Record<SurfaceId, number>>;
  surfaceSlip: Partial<Record<SurfaceId, number>>;
  /** Lateral vs longitudinal peak µ ratio. */
  lateralGripRatio: number;
  compound: 'gravel' | 'tarmac';
}

export interface EngineSpec {
  idleRpm: number;
  limiterRpm: number;
  /** Full-boost wide-open-throttle torque curve [rpm, N·m]. */
  torqueCurve: [number, number][];
  /** Fraction of full-boost torque available at zero boost. */
  naTorqueFraction: number;
  inertia: number;
  /** Friction torque: c0 + c1*rpm/1000 (N·m) and closed-throttle pumping torque at limiter. */
  frictionTorque: [number, number];
  engineBrakeTorque: number;
  turbo: {
    spoolUpTime: number;
    spoolDownTime: number;
    /** rpm where exhaust flow starts to build boost / reaches full boost at WOT. */
    boostStartRpm: number;
    boostFullRpm: number;
    /** Anti-lag boost level held off-throttle (0..1) above antiLagMinRpm. */
    antiLagBoost: number;
    antiLagMinRpm: number;
  };
}

export interface GearboxSpec {
  ratios: number[];
  reverse: number;
  finalDrive: number;
  /** Ignition-cut / gear change time (s). */
  shiftTime: number;
  /** Driveline efficiency (0..1). */
  efficiency: number;
  autoUpRpm: number;
  autoDownRpm: number;
}

export interface DiffSpec {
  preload: number;
  /** Locking torque per N·m of input torque, on power and on coast. */
  powerRamp: number;
  coastRamp: number;
}

export interface CarSpec {
  id: string;
  name: string;
  hidden?: boolean;
  description: string;
  model?: string;
  sources: { claim: string; url?: string; derived?: string }[];
  chassis: {
    /** Total mass including crew (kg). */
    mass: number;
    /** Principal inertia about car-local X (pitch), Y (yaw), Z (roll), kg·m². */
    inertia: Vec3Tuple;
    /** Centre of mass in car-local coordinates. */
    centerOfMass: Vec3Tuple;
    /** Collision hull half-extents and shape points (car local). */
    hull: Vec3Tuple[];
    dragArea: number;
    /** Lift coefficient × area at front/rear axles (negative = downforce). */
    liftAreaFront: number;
    liftAreaRear: number;
    rollCenterHeight: number;
  };
  wheels: WheelSpec[];
  tyre: TyreSpec;
  suspension: { front: SuspensionSpec; rear: SuspensionSpec };
  steering: {
    maxAngleDeg: number;
    /** Steering wheel degrees per road-wheel degree. */
    ratio: number;
    ackermann: number;
  };
  engine: EngineSpec;
  gearbox: GearboxSpec;
  /** engage/fullRpm: wheel-driven engine speed range over which the clutch locks; launchRpm: slip rpm at full throttle from rest. */
  clutch: { maxTorque: number; engageRpm: number; fullRpm: number; launchRpm: number };
  diffs: { front: DiffSpec; rear: DiffSpec };
  brakes: { maxTorque: number; frontBias: number; handbrakeTorque: number };
}

/** Driver input sampled every simulation step. All values already filtered (see input smoothing). */
export interface ControlInput {
  /** -1 = full left, +1 = full right. */
  steer: number;
  throttle: number;
  brake: number;
  handbrake: number;
  shiftUp: boolean;
  shiftDown: boolean;
}

export interface Assists {
  autoGearbox: boolean;
  abs: boolean;
  tractionControl: boolean;
  steeringHelp: boolean;
  /** Extra steering smoothing (rookie) on top of digital-key smoothing. */
  steeringSmoothing: boolean;
}

export const ROOKIE_ASSISTS: Assists = {
  autoGearbox: true,
  abs: true,
  tractionControl: true,
  steeringHelp: true,
  steeringSmoothing: true,
};

export const REALISTIC_ASSISTS: Assists = {
  autoGearbox: false,
  abs: false,
  tractionControl: false,
  steeringHelp: false,
  steeringSmoothing: false,
};

export const neutralInput = (): ControlInput => ({
  steer: 0,
  throttle: 0,
  brake: 0,
  handbrake: 0,
  shiftUp: false,
  shiftDown: false,
});
